import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { translations, type Lang, type Translations } from "./translations";
import { findCertificateBySlug, otherLangSlug } from "@/data/certificateSlugs";

interface LangContextValue {
  lang: Lang;
  t: Translations;
  prefix: string; // "" or "/en"
  switchHref: string; // path to switch language
}

const LangContext = createContext<LangContextValue | null>(null);

export const LangProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const value = useMemo<LangContextValue>(() => {
    const isEn = location.pathname === "/en" || location.pathname.startsWith("/en/");
    const lang: Lang = isEn ? "en" : "sv";
    const prefix = isEn ? "/en" : "";
    // Certificate detail page slug translation
    const certMatchSv = location.pathname.match(/^\/lakarintyg\/([^/]+)\/?$/);
    const certMatchEn = location.pathname.match(/^\/en\/medical-certificates\/([^/]+)\/?$/);
    if (certMatchSv) {
      const internal = findCertificateBySlug(certMatchSv[1], "sv");
      const enSlug = internal ? otherLangSlug(internal, "en") : null;
      const switchHref = enSlug ? `/en/medical-certificates/${enSlug}` : "/en/lakarintyg";
      return { lang, t: translations[lang], prefix, switchHref };
    }
    if (certMatchEn) {
      const internal = findCertificateBySlug(certMatchEn[1], "en");
      const svSlug = internal ? otherLangSlug(internal, "sv") : null;
      const switchHref = svSlug ? `/lakarintyg/${svSlug}` : "/lakarintyg";
      return { lang, t: translations[lang], prefix, switchHref };
    }
    // Guide detail pages
    const guideMatchSv = location.pathname.match(/^\/guider\/([^/]+)\/([^/]+)\/?$/);
    const guideMatchEn = location.pathname.match(/^\/en\/guides\/([^/]+)\/([^/]+)\/?$/);
    if (guideMatchSv) {
      const switchHref = `/en/guides/${guideMatchSv[1]}/${guideMatchSv[2]}`;
      return { lang, t: translations[lang], prefix, switchHref };
    }
    if (guideMatchEn) {
      const switchHref = `/guider/${guideMatchEn[1]}/${guideMatchEn[2]}`;
      return { lang, t: translations[lang], prefix, switchHref };
    }
    const swedishToEnglishPath: Record<string, string> = {
      "/kontakt": "/contact",
      "/boka-tid": "/book-appointment",
      "/fragor-och-svar": "/faq",
      "/infor-besoket": "/before-visit",
      "/sa-fungerar-det": "/how-it-works",
      "/guider": "/guides",
      "/foretag": "/companies",
      "/foretag/arbetsmiljo": "/companies/occupational-health",
      "/foretag/drogtestning": "/companies/drug-testing",
      "/foretag/tjanster": "/companies/services",
      "/foretag/kontakt": "/companies/contact",
      "/foretag/boka-tid": "/companies/book",
      "/halsokontroller": "/health-checks",
      "/om-oss": "/about-us",
      "/integritetspolicy": "/privacy-policy",
      "/cookie-policy": "/cookie-policy",
      "/anvandarvillkor": "/terms-of-use",
      "/bekraftelse": "/confirmation",
      "/starta-arende": "/start-case",
      "/provtagning": "/sample-collection",
      "/drogtestning": "/drug-testing",
      "/drogtestning/privat": "/drug-testing/private",
      "/drogtestning/for-intyg": "/drug-testing/for-certificate",
      "/drogtestning/forfragan": "/drug-testing/enquiry",
      "/drogtestning/boka/salivtest": "/drug-testing/book/saliva",
      "/drogtestning/boka/urinprov": "/drug-testing/book/urine",
    };
    const englishToSwedishPath: Record<string, string> = {
      "/contact": "/kontakt",
      "/book-appointment": "/boka-tid",
      "/faq": "/fragor-och-svar",
      "/before-visit": "/infor-besoket",
      "/how-it-works": "/sa-fungerar-det",
      "/guides": "/guider",
      "/companies": "/foretag",
      "/companies/occupational-health": "/foretag/arbetsmiljo",
      "/companies/drug-testing": "/foretag/drogtestning",
      "/companies/services": "/foretag/tjanster",
      "/companies/contact": "/foretag/kontakt",
      "/companies/book": "/foretag/boka-tid",
      "/health-checks": "/halsokontroller",
      "/about-us": "/om-oss",
      "/privacy-policy": "/integritetspolicy",
      "/cookie-policy": "/cookie-policy",
      "/terms-of-use": "/anvandarvillkor",
      "/confirmation": "/bekraftelse",
      "/start-case": "/starta-arende",
      "/sample-collection": "/provtagning",
      "/drug-testing": "/drogtestning",
      "/drug-testing/private": "/drogtestning/privat",
      "/drug-testing/for-certificate": "/drogtestning/for-intyg",
      "/drug-testing/enquiry": "/drogtestning/forfragan",
      "/drug-testing/book/saliva": "/drogtestning/boka/salivtest",
      "/drug-testing/book/urine": "/drogtestning/boka/urinprov",
    };
    const rest = isEn ? location.pathname.replace(/^\/en/, "") || "/" : location.pathname;
    const mappedRest = isEn
      ? (englishToSwedishPath[rest] ?? rest)
      : (swedishToEnglishPath[location.pathname] ?? location.pathname);
    const switchPath = isEn
      ? mappedRest === "/" ? "/" : mappedRest
      : `/en${mappedRest === "/" ? "" : mappedRest}`;
    const switchHref = `${switchPath}${location.search}${location.hash}`;
    return { lang, t: translations[lang], prefix, switchHref };
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    document.documentElement.lang = value.lang;
  }, [value.lang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LangProvider");
  return ctx;
};